/**
 * Registo de atividade do sistema (auditoria legível) → Firestore `systemActivity`.
 * Escrita: qualquer utilizador autenticado, com actorUid == auth.uid (anti-spoof).
 * Leitura: painel admin (regras Firestore).
 *
 * Ver inventário cruzado: `app/docs/LOGGING-AUDIT.md` (outros logs: Messa:IO, timelines CRM, notifications).
 */

import { messaOpLog, messaFirestoreErrBrief } from './messaOperationalLog.js';

/** @type {Map<string, number>} */
const _dedupeAt = new Map();
const DEDUPE_DEFAULT_MS = 4000;
const LOGIN_DEDUPE_MS = 2 * 60 * 1000;

/**
 * @param {string} key
 * @param {number} windowMs
 * @returns {boolean} true se deve ignorar (duplicado recente)
 */
function shouldSkipDedupe(key, windowMs) {
  if (!key) return false;
  const now = Date.now();
  const prev = _dedupeAt.get(key);
  if (prev != null && now - prev < windowMs) return true;
  _dedupeAt.set(key, now);
  if (_dedupeAt.size > 200) {
    const cutoff = now - 120000;
    for (const [k, t] of _dedupeAt) {
      if (t < cutoff) _dedupeAt.delete(k);
    }
  }
  return false;
}

export function getActivityActorName() {
  const u = typeof window !== 'undefined' ? window._currentUser : null;
  if (!u) return 'Alguém';
  const cache = typeof window !== 'undefined' ? window._usersCache : null;
  const fromCache = Array.isArray(cache) ? cache.find((x) => x.id === u.uid) : null;
  const n = String(fromCache?.displayName || u.displayName || '').trim();
  if (n) return n.slice(0, 120);
  const em = typeof u.email === 'string' ? u.email.trim() : '';
  if (em) return (em.split('@')[0] || em).slice(0, 120);
  return 'Alguém';
}

/**
 * Nome para o texto "… entrou no sistema": prioridade `firstName`/`lastName` no doc `users`,
 * senão primeiro token + resto do displayName (Nome + Sobrenome composto).
 */
export function getLoginAuditDisplayName() {
  const u = typeof window !== 'undefined' ? window._currentUser : null;
  if (!u) return 'Alguém';
  const cache = typeof window !== 'undefined' ? window._usersCache : null;
  const row = Array.isArray(cache) ? cache.find((x) => x.id === u.uid) : null;
  const fn = String(row?.firstName || '').trim();
  const ln = String(row?.lastName || '').trim();
  if (fn && ln) return `${fn} ${ln}`.replace(/\s+/g, ' ').trim().slice(0, 120);
  if (fn) return fn.slice(0, 120);
  if (ln) return ln.slice(0, 120);

  const raw = String(row?.displayName || u.displayName || '').trim();
  if (raw) {
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'Alguém';
    if (parts.length === 1) return parts[0].slice(0, 120);
    return `${parts[0]} ${parts.slice(1).join(' ')}`.slice(0, 120);
  }
  const em = typeof u.email === 'string' ? u.email.trim() : '';
  if (em) return (em.split('@')[0] || em).slice(0, 120);
  return 'Alguém';
}

/**
 * @param {{
 *   category: 'client'|'agenda'|'sazonal'|'admin'|'finance'|'gcal'|'login'|'data_main'|'scripts'|'agenda_settings'|'seasonal_settings'|'calendar_sync',
 *   verb: 'create'|'edit'|'delete'|'status'|'approve'|'import'|'restore'|'sync'|'login'|'role'|'block'|'fail',
 *   actionKey: string,
 *   summary: string,
 *   entityType?: string,
 *   entityId?: string,
 *   entityLabel?: string,
 *   changeTags?: string[],
 *   changeDiff?: Array<{ label: string, before: string, after: string, removal?: boolean }>,
 *   dedupeKey?: string,
 *   dedupeWindowMs?: number,
 *   actorType?: 'user'|'system',
 *   dataMainEvent?: string,
 *   dataMainReason?: string,
 *   dataMainOrigin?: string,
 *   dataMainLocalUpdatedAt?: number | null,
 *   dataMainRemoteUpdatedAt?: number | null,
 *   dataMainImpactGroups?: number | null,
 *   dataMainImpactItems?: number | null,
 *   dataMainImpactClients?: number | null,
 *   actorDisplayName?: string,
 *   suspectedRollback?: boolean,
 *   suspectedRollbackReasons?: string[],
 *   suspectedRollbackScore?: number,
 *   suspectedRollbackSignals?: Array<{ code?: string, label?: string, weight?: number }>,
 * }} p
 */
export async function logSystemActivity(p) {
  if (typeof window === 'undefined') return;
  const u = window._currentUser;
  if (!u || !window._fbDb || !window._fbCollection || !window._fbAddDoc) return;

  const dedupeMs = p.dedupeWindowMs ?? DEDUPE_DEFAULT_MS;
  const dk = p.dedupeKey || `${p.actionKey}:${p.entityId || ''}:${u.uid}`;
  if (shouldSkipDedupe(dk, dedupeMs)) return;

  const actorName =
    p.actorType === 'system'
      ? 'Sistema'
      : String(p.actorDisplayName || '').trim() || getActivityActorName();
  const summary = String(p.summary || '').trim().slice(0, 500);
  if (!summary) return;

  const tagsForSearch = Array.isArray(p.changeTags)
    ? p.changeTags.map((t) => String(t || '').trim()).filter(Boolean)
    : [];
  const rollbackHint =
    p.suspectedRollback && Array.isArray(p.suspectedRollbackReasons)
      ? p.suspectedRollbackReasons.join(' ')
      : '';
  const searchBlob = [
    actorName,
    summary,
    p.entityType || '',
    p.entityId || '',
    p.entityLabel || '',
    p.actionKey || '',
    p.dataMainEvent || '',
    p.dataMainReason || '',
    p.dataMainOrigin || '',
    rollbackHint,
    p.suspectedRollback ? 'rollback suspeito suspected_rollback' : '',
    tagsForSearch.join(' '),
    ...(Array.isArray(p.changeDiff)
      ? p.changeDiff.flatMap((d) => [d.label, d.before, d.after])
      : []),
  ]
    .join(' ')
    .toLowerCase()
    .slice(0, 2000);

  const changeTags =
    tagsForSearch.length > 0 ? tagsForSearch.slice(0, 20).map((t) => t.slice(0, 48)) : undefined;

  let changeDiffOut;
  if (Array.isArray(p.changeDiff) && p.changeDiff.length) {
    changeDiffOut = p.changeDiff.slice(0, 24).map((row) => {
      const o = {
        label: String(row.label || '').slice(0, 80),
        before: String(row.before ?? '').slice(0, 400),
        after: String(row.after ?? '').slice(0, 400),
      };
      if (row.removal === true) o.removal = true;
      return o;
    });
  }

  const at = Date.now();
  try {
    const doc = {
      at,
      actorUid: u.uid,
      actorName: actorName.slice(0, 120),
      category: p.category,
      verb: p.verb,
      actionKey: String(p.actionKey || '').slice(0, 80),
      summary,
      entityType: p.entityType ? String(p.entityType).slice(0, 40) : '',
      entityId: p.entityId ? String(p.entityId).slice(0, 120) : '',
      searchBlob,
    };
    const el = p.entityLabel != null && String(p.entityLabel).trim();
    if (el) doc.entityLabel = String(p.entityLabel).trim().slice(0, 200);
    if (changeTags && changeTags.length) doc.changeTags = changeTags;
    if (changeDiffOut && changeDiffOut.length) doc.changeDiff = changeDiffOut;

    if (p.actorType === 'system') doc.actorType = 'system';
    else doc.actorType = 'user';

    if (p.dataMainEvent) doc.dataMainEvent = String(p.dataMainEvent).slice(0, 64);
    if (p.dataMainReason != null && String(p.dataMainReason).trim()) {
      doc.dataMainReason = String(p.dataMainReason).trim().slice(0, 120);
    }
    if (p.dataMainOrigin != null && String(p.dataMainOrigin).trim()) {
      doc.dataMainOrigin = String(p.dataMainOrigin).trim().slice(0, 120);
    }
    if (p.dataMainLocalUpdatedAt != null && typeof p.dataMainLocalUpdatedAt === 'number') {
      doc.dataMainLocalUpdatedAt = p.dataMainLocalUpdatedAt;
    }
    if (p.dataMainRemoteUpdatedAt != null && typeof p.dataMainRemoteUpdatedAt === 'number') {
      doc.dataMainRemoteUpdatedAt = p.dataMainRemoteUpdatedAt;
    }
    if (p.dataMainImpactGroups != null && typeof p.dataMainImpactGroups === 'number') {
      doc.dataMainImpactGroups = p.dataMainImpactGroups;
    }
    if (p.dataMainImpactItems != null && typeof p.dataMainImpactItems === 'number') {
      doc.dataMainImpactItems = p.dataMainImpactItems;
    }
    if (p.dataMainImpactClients != null && typeof p.dataMainImpactClients === 'number') {
      doc.dataMainImpactClients = p.dataMainImpactClients;
    }

    if (p.suspectedRollback === true) {
      doc.suspectedRollback = true;
      if (Array.isArray(p.suspectedRollbackReasons) && p.suspectedRollbackReasons.length) {
        doc.suspectedRollbackReasons = p.suspectedRollbackReasons
          .map((x) => String(x || '').trim().slice(0, 120))
          .filter(Boolean)
          .slice(0, 12);
      }
      if (p.suspectedRollbackScore != null && typeof p.suspectedRollbackScore === 'number') {
        doc.suspectedRollbackScore = Math.min(999, Math.max(0, Math.round(p.suspectedRollbackScore)));
      }
      if (Array.isArray(p.suspectedRollbackSignals) && p.suspectedRollbackSignals.length) {
        doc.suspectedRollbackSignals = p.suspectedRollbackSignals.slice(0, 16).map((s) => ({
          code: String(s?.code || '').slice(0, 48),
          label: String(s?.label || '').slice(0, 120),
          weight: typeof s?.weight === 'number' ? Math.min(999, Math.max(0, Math.round(s.weight))) : 0,
        }));
      }
    }

    await window._fbAddDoc(window._fbCollection(window._fbDb, 'systemActivity'), doc);
  } catch (e) {
    messaOpLog('system-activity', 'error', '[Messa] systemActivity addDoc falhou', messaFirestoreErrBrief(e));
    console.error('[Messa] systemActivity log falhou — ver regras Firestore e rede', e);
  }
}

/**
 * Login: uma vez por sessão do separador (sessionStorage); novo separador = novo evento.
 */
export function logSystemLoginOncePerSession() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
  const u = window._currentUser;
  if (!u) return;
  const r = String(window._currentRole || '').trim();
  // Todos os papéis reais logam (exclui só pending/rejected/sem papel).
  // (3.9) Era uma lista fixa dos seis papéis padrão — um papel CUSTOM criado no painel
  // caía fora e o login dessa pessoa não entrava no log de atividade.
  if (!r || r === 'pending' || r === 'rejected') return;

  const k = `messa_login_logged:${u.uid}`;
  if (sessionStorage.getItem(k) === '1') return;
  sessionStorage.setItem(k, '1');

  const name = getLoginAuditDisplayName();
  void logSystemActivity({
    category: 'login',
    verb: 'login',
    actionKey: 'session.login',
    actorDisplayName: name,
    summary: `${name} entrou no sistema`,
    dedupeKey: `login:${u.uid}`,
    dedupeWindowMs: LOGIN_DEDUPE_MS,
  });
}

export function clearActivityLoginSessionMarker() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('messa_login_logged:')) sessionStorage.removeItem(key);
    }
  } catch (_) {}
}

if (typeof window !== 'undefined') {
  window.logSystemActivity = logSystemActivity;
  window.logSystemLoginOncePerSession = logSystemLoginOncePerSession;
  window.clearActivityLoginSessionMarker = clearActivityLoginSessionMarker;
}
